import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, Section } from "../../components/Common";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, BellRing } from "lucide-react";
import { formatCurrency } from "../../lib/currency";
import { useDataSync } from "../../lib/realtime";

const STATUSES = ["pending", "paid", "overdue"];
const COLORS = {
  pending: "bg-amber-50 text-amber-700",
  paid: "bg-emerald-50 text-emerald-700",
  overdue: "bg-rose-50 text-rose-700",
};

export default function AdminFees() {
  const [fees, setFees] = useState([]);
  const [students, setStudents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // fee being edited
  const [form, setForm] = useState({ student_id: "", amount: "", due_date: "", description: "" });

  const load = async () => {
    const [f, s] = await Promise.all([
      api.get("/admin/fees"),
      api.get("/admin/users", { params: { role: "student" } }),
    ]);
    setFees(f.data);
    setStudents(s.data);
  };
  useEffect(() => { load(); }, []);
  useDataSync("fees", () => load());

  const studentName = (id) => students.find((s) => s.id === id)?.name || id;

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/admin/fees/${editing.id}`, {
          amount: parseFloat(form.amount),
          due_date: form.due_date,
          description: form.description,
        });
        toast.success("Fee updated");
      } else {
        await api.post("/admin/fees", { ...form, amount: parseFloat(form.amount) });
        toast.success("Fee added");
      }
      setShowForm(false); setEditing(null);
      setForm({ student_id: "", amount: "", due_date: "", description: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startEdit = (fee) => {
    setEditing(fee);
    setForm({
      student_id: fee.student_id,
      amount: String(fee.amount),
      due_date: fee.due_date,
      description: fee.description || "",
    });
    setShowForm(true);
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/fees/${id}`, { status });
    toast.success("Updated");
    load();
  };

  const remove = async (id) => {
    if (!confirm("Delete this fee?")) return;
    try {
      await api.delete(`/admin/fees/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const collected = fees.filter((f) => f.status === "paid").reduce((s, f) => s + Number(f.amount || 0), 0);
  const pending = fees.filter((f) => f.status !== "paid").reduce((s, f) => s + Number(f.amount || 0), 0);

  return (
    <div>
      <PageHeader
        title="Fee tracking"
        subtitle="Track paid, pending, and overdue fees. All amounts in Indian Rupees (₹)."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/fees/send-reminders");
                  toast.success(
                    data.fees_reminded
                      ? `Reminders sent for ${data.fees_reminded} fee${data.fees_reminded === 1 ? "" : "s"} (${data.emails_sent} email${data.emails_sent === 1 ? "" : "s"})`
                      : "No unpaid fees due in the next 3 days"
                  );
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
              data-testid="btn-send-fee-reminders"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-blue-50 hover:text-blue-700 transition-colors">
              <BellRing className="h-4 w-4" /> Send due reminders
            </button>
            <button onClick={() => { setShowForm((v) => !v); setEditing(null); setForm({ student_id: "", amount: "", due_date: "", description: "" }); }}
            data-testid="btn-add-fee"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Add fee
          </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Collected</div>
          <div className="mt-2 font-heading font-black text-3xl text-emerald-600">{formatCurrency(collected)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Pending</div>
          <div className="mt-2 font-heading font-black text-3xl text-amber-600">{formatCurrency(pending)}</div>
        </div>
      </div>

      {showForm && (
        <Section title={editing ? "Edit fee" : "New fee"} actions={
          <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        }>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="fee-form">
            <div>
              <label className="text-sm font-semibold text-slate-700">Student</label>
              <select required disabled={!!editing} value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })}
                data-testid="fee-student" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none disabled:bg-slate-50">
                <option value="">— select —</option>
                {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Amount (₹)</label>
              <input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="fee-amount" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Due date</label>
              <input required type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="fee-due" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Description</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div className="sm:col-span-2 flex gap-2">
              <button type="submit" data-testid="fee-submit" className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
                {editing ? "Save changes" : "Add fee"}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </Section>
      )}

      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Student</th>
              <th className="text-left px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Due</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Description</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {fees.map((f) => (
              <tr key={f.id} data-testid={`fee-row-${f.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">{studentName(f.student_id)}</td>
                <td className="px-4 py-3 font-mono">{formatCurrency(f.amount, f.currency)}</td>
                <td className="px-4 py-3 text-slate-600">{f.due_date}</td>
                <td className="px-4 py-3">
                  <select value={f.status} onChange={(e) => updateStatus(f.id, e.target.value)}
                    data-testid={`fee-status-${f.id}`}
                    className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full ${COLORS[f.status]} border-0 outline-none`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">{f.description}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => startEdit(f)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg" data-testid={`edit-fee-${f.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(f.id)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded-lg" data-testid={`delete-fee-${f.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {fees.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No fees yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
